import React, { CSSProperties, MouseEventHandler, MutableRefObject, useCallback, useRef, useState } from "react";
import { Modal, Button } from "@clarkinc/zest-design-system";
import formatAsCurrency from "@wss/utilities/formatAsCurrency";
import { PricingProduct } from "~/ProductDetails/Pricing/PricingProduct";
import ShieldActive from "./shieldactive.svg";
import ShieldInactive from "./shieldinactive.svg";
import { ExtendedWarranty } from "~/ProductDetails/ProductDetailPageResult";
import { Icon, Popover, PopoverContent, PopoverBody, PopoverTrigger } from "@clarkinc/zest-design-system/ui";
interface Props {
  hasSelectedGroupMember: boolean;
  handleAddProtection: (event: React.SyntheticEvent) => void;
  handleRemoveProtection: (event: React.SyntheticEvent) => void;
  product: PricingProduct;
  selectedWarranty?: ExtendedWarranty;
  warrantyInfo: string;
}
const ExtendedWarrantyComponent = ({
  hasSelectedGroupMember,
  handleAddProtection,
  handleRemoveProtection,
  product,
  selectedWarranty,
  warrantyInfo
}: Props) => {
  const {
    extendedWarranties
  } = product;
  const extendedWarranty = extendedWarranties[0];
  const extendedWarrantyPrice = extendedWarranty?.price ?? 0;
  const [open, setOpen] = useState(false);
  const mouseOverRef: MutableRefObject<HTMLElement | null> = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const handleMouseEnter: MouseEventHandler<HTMLButtonElement | HTMLDivElement> = useCallback((e): void => {
    mouseOverRef.current = e.target as HTMLElement;
    setOpen(true);
  }, []);
  const handleMouseTriggerLeave = useCallback((): void => {
    // if mouse leaves the trigger and enters the content, don't close the popover
    if (contentRef.current?.contains(mouseOverRef.current)) {
      return;
    }
    setOpen(false);
  }, [contentRef]);
  const handleMouseContentLeave = useCallback((): void => {
    // if mouse leaves the content and enters the trigger, don't close the popover
    if (triggerRef.current?.contains(mouseOverRef.current)) {
      return;
    }
    setOpen(false);
  }, [triggerRef]);
  const buttonEnabledStyle: CSSProperties = {
    width: "110px",
    cursor: "pointer"
  };
  const buttonEnabledCSSClass = "bg-origin-box-border bg-repeat-x border-solid border box-border cursor-pointer inline-block font-semibold text-center no-underline hover:no-underline antialiased hover:bg-position-y-15 flex-shrink-0 ml-auto rounded text-xs leading-3 py-1 px-2 bg-green-500 bg-green-500 hover:bg-green-800 border-none text-white hover:text-white align-middle";
  const removeProtection = (e: React.SyntheticEvent) => {
    handleRemoveProtection(e);
    document.getElementById("addProtectionButton")?.focus();
  };
  return <div className="
              bg-white
              border-gray-500
              rounded
              box-border
              flex
              items-center
              flex-1
              mx-auto
              mb-2 py-3 w-full" id="whatWeOfferExtendedWarranty" data-sentry-component="ExtendedWarrantyComponent" data-sentry-source-file="ExtendedWarrantyComponent.tsx">
      <span className="flex-shrink-0 h-10 w-10" id="extendedWarrantyShieldIconInactive" hidden={!!selectedWarranty}>
        <img alt="shield icon" className="h-10 w-10" src={ShieldInactive} />
      </span>
      <span className="flex-shrink-0 h-10 w-10" id="extendedWarrantyShieldIconActive" hidden={!selectedWarranty}>
        <img alt="shield icon" className="h-10 w-10" src={ShieldActive} />
      </span>
      <div className="px-2">
        <h4 className="leading-3 mb-1" data-testid="proetct-your-product">
          Protect Your Product
        </h4>
        <p className="
                  font-normal
                  text-xs
                  leading-none
                  mb-0
                  text-left
                  text-gray-800" id="extendedWarrantyInfoTextInactive" data-testid="extendedWarranty-InfoText-Inactive">
          {"Coverage starting at "}
          <span id="coverageStartingAtPrice">
            {formatAsCurrency.formatIfUndefined(extendedWarrantyPrice)}
          </span>
          &nbsp;
          <Popover open={open} onOpenChange={setOpen} data-sentry-element="Popover" data-sentry-source-file="ExtendedWarrantyComponent.tsx">
            <PopoverTrigger aria-label="more info about warranty" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseTriggerLeave} ref={triggerRef} className="border-0 bg-transparent align-sub p-0" data-sentry-element="PopoverTrigger" data-sentry-source-file="ExtendedWarrantyComponent.tsx">
              <Icon name="ri-question-fill" size="xsmall" className="fill-green-500 px-1" theme="wss" data-sentry-element="Icon" data-sentry-source-file="ExtendedWarrantyComponent.tsx" />
            </PopoverTrigger>
            <PopoverContent onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseContentLeave} ref={contentRef} data-sentry-element="PopoverContent" data-sentry-source-file="ExtendedWarrantyComponent.tsx">
              <PopoverBody data-sentry-element="PopoverBody" data-sentry-source-file="ExtendedWarrantyComponent.tsx">{warrantyInfo}</PopoverBody>
            </PopoverContent>
          </Popover>
        </p>
        <p className="
                      font-normal
                      text-xs
                      leading-none
                      mb-0
                      text-left
                      text-gray-800" id="extendedWarrantyInfoTextActive" data-testid="extendedWarranty-InfoText-Active" hidden={!selectedWarranty}>
          <span id="extendedWarrantyInfoTextSelectedTermName">
            {selectedWarranty?.name}
          </span>
          &nbsp;
          <Modal.Trigger type="button" id="extendedWarrantyInfoTextEditWarranty" className="text-green-500 underline" data-testid="warranty-edit" htmlFor="extended-warranty-modal-dialog" data-sentry-element="unknown" data-sentry-source-file="ExtendedWarrantyComponent.tsx">
            Edit
          </Modal.Trigger>
          &nbsp;&nbsp;
          <Button type="button" id="extendedWarrantyInfoTextRemoveWarranty" className="underline text-green-500 bg-white" onClick={removeProtection} data-testid="warranty-remove" theme="headless" data-sentry-element="Button" data-sentry-source-file="ExtendedWarrantyComponent.tsx">
            Remove
          </Button>
        </p>
      </div>
      {!selectedWarranty && <Modal.Trigger onClick={handleAddProtection} className={buttonEnabledCSSClass} style={buttonEnabledStyle} htmlFor="extended-warranty-modal-dialog" type={hasSelectedGroupMember ? "submit" : "button"} id="addProtectionButton">
          Add Protection
        </Modal.Trigger>}
    </div>;
};
export default ExtendedWarrantyComponent;